/**
 * A11y Utilities - Accessibility Helper Functions
 * Implements WCAG 2.2 compliant utilities for:
 * - ARIA Live Region Announcer (WCAG 4.1.3 Status Messages)
 * - Focus Trap for Modals/Overlays (WCAG 2.1.2 No Keyboard Trap)
 */

const A11yUtils = (function () {
  'use strict'

  /**
   * ARIA Live Region Announcer
   * Creates and manages a live region for screen reader announcements
   * WCAG 4.1.3: Status Messages
   */
  const LiveAnnouncer = (function () {
    let liveRegion = null

    /**
     * Initialize the live region if it doesn't exist
     */
    function init() {
      if (!liveRegion) {
        liveRegion = document.createElement('div')
        liveRegion.setAttribute('role', 'status')
        liveRegion.setAttribute('aria-live', 'polite')
        liveRegion.setAttribute('aria-atomic', 'true')
        liveRegion.className = 'sr-only'
        liveRegion.style.cssText =
          'position:absolute;left:-10000px;width:1px;height:1px;overflow:hidden;'
        document.body.appendChild(liveRegion)
      }
    }

    /**
     * Announce a message to screen readers
     * @param {string} message - The message to announce
     * @param {string} priority - 'polite' (default) or 'assertive'
     */
    function announce(message, priority = 'polite') {
      init()

      // Update aria-live attribute based on priority
      liveRegion.setAttribute('aria-live', priority)

      // Clear and set message with small delay to ensure announcement
      liveRegion.textContent = ''
      setTimeout(() => {
        liveRegion.textContent = message
      }, 100)

      // Clear message after announcement (optional)
      setTimeout(() => {
        liveRegion.textContent = ''
      }, 3000)
    }

    return {
      announce: announce,
    }
  })()

  /**
   * Focus Trap Utility
   * Manages focus within modal dialogs and overlays
   * WCAG 2.1.2: No Keyboard Trap
   * WCAG 2.4.3: Focus Order
   */
  const FocusTrap = (function () {
    const traps = new Map()

    /**
     * Get all focusable elements within a container
     * @param {HTMLElement} container - The container element
     * @returns {Array} Array of focusable elements
     */
    function getFocusableElements(container) {
      const focusableSelectors = [
        'a[href]',
        'button:not([disabled])',
        'textarea:not([disabled])',
        'input:not([disabled]):not([type="hidden"])',
        'select:not([disabled])',
        '[tabindex]:not([tabindex="-1"])',
        '[contenteditable="true"]',
      ].join(', ')

      return Array.from(container.querySelectorAll(focusableSelectors)).filter(
        (el) => {
          return (
            el.offsetWidth > 0 ||
            el.offsetHeight > 0 ||
            el.getClientRects().length > 0
          )
        },
      )
    }

    /**
     * Create a focus trap for a container
     * @param {HTMLElement|string} container - The container element or selector
     * @param {Object} options - Configuration options
     * @returns {Object} Trap instance with activate/deactivate methods
     */
    function create(container, options = {}) {
      const element =
        typeof container === 'string'
          ? document.querySelector(container)
          : container

      if (!element) {
        console.warn('Focus trap container not found')
        return null
      }

      const config = {
        initialFocus: options.initialFocus || null,
        returnFocus: options.returnFocus !== false,
        escapeDeactivates: options.escapeDeactivates !== false,
        onActivate: options.onActivate || null,
        onDeactivate: options.onDeactivate || null,
      }

      let previouslyFocusedElement = null
      let isActive = false

      /**
       * Handle tab key navigation within trap
       */
      function handleTabKey(e) {
        if (!isActive || e.key !== 'Tab') return

        const focusableElements = getFocusableElements(element)
        if (focusableElements.length === 0) return

        const firstElement = focusableElements[0]
        const lastElement = focusableElements[focusableElements.length - 1]

        // Shift + Tab on first element -> focus last element
        if (e.shiftKey && document.activeElement === firstElement) {
          e.preventDefault()
          lastElement.focus()
        }
        // Tab on last element -> focus first element
        else if (!e.shiftKey && document.activeElement === lastElement) {
          e.preventDefault()
          firstElement.focus()
        }
      }

      /**
       * Handle escape key to deactivate
       */
      function handleEscapeKey(e) {
        if (!isActive || !config.escapeDeactivates || e.key !== 'Escape') return
        deactivate()
      }

      /**
       * Activate the focus trap
       */
      function activate() {
        if (isActive) return

        // Store currently focused element
        previouslyFocusedElement = document.activeElement

        // Add event listeners
        document.addEventListener('keydown', handleTabKey)
        if (config.escapeDeactivates) {
          document.addEventListener('keydown', handleEscapeKey)
        }

        // Set initial focus
        const focusableElements = getFocusableElements(element)
        if (focusableElements.length > 0) {
          const initialFocusEl =
            config.initialFocus ||
            element.querySelector('[data-autofocus]') ||
            focusableElements[0]

          if (initialFocusEl) {
            setTimeout(() => initialFocusEl.focus(), 100)
          }
        }

        isActive = true

        // Call onActivate callback
        if (config.onActivate) {
          config.onActivate()
        }
      }

      /**
       * Deactivate the focus trap
       */
      function deactivate() {
        if (!isActive) return

        // Remove event listeners
        document.removeEventListener('keydown', handleTabKey)
        document.removeEventListener('keydown', handleEscapeKey)

        // Return focus to previously focused element (se ancora esiste ed è focusable)
        if (config.returnFocus && previouslyFocusedElement) {
          setTimeout(() => {
            try {
              const active = previouslyFocusedElement
              if (
                active &&
                typeof active.focus === 'function' &&
                document.contains(active)
              ) {
                active.focus()
              }
            } catch (e) {
              // ignore focus errors (es. elementi rimossi dal DOM)
            }
          }, 100)
        }

        isActive = false

        // Call onDeactivate callback
        if (config.onDeactivate) {
          config.onDeactivate()
        }
      }

      const trap = {
        activate,
        deactivate,
        isActive: () => isActive,
      }

      // Store trap instance
      const trapId = Date.now().toString()
      traps.set(trapId, trap)

      return trap
    }

    /**
     * Deactivate all active traps
     */
    function deactivateAll() {
      traps.forEach((trap) => {
        if (trap.isActive()) {
          trap.deactivate()
        }
      })
    }

    return {
      create: create,
      deactivateAll: deactivateAll,
    }
  })()

  // Public API
  return {
    LiveAnnouncer: LiveAnnouncer,
    FocusTrap: FocusTrap,
  }
})()

// Make available globally
if (typeof window !== 'undefined') {
  window.A11yUtils = A11yUtils
}

// Stili mappa globali
const FEDLUS_MAP_STYLE = [
  {
    featureType: 'administrative.land_parcel',
    elementType: 'labels',
    stylers: [
      {
        visibility: 'off',
      },
    ],
  },
  {
    featureType: 'landscape',
    stylers: [
      {
        saturation: -45,
      },
      {
        lightness: 70,
      },
    ],
  },
  {
    featureType: 'poi',
    elementType: 'labels.icon',
    stylers: [
      {
        saturation: 45,
      },
      {
        visibility: 'off',
      },
    ],
  },
  {
    featureType: 'poi',
    elementType: 'labels.text',
    stylers: [
      {
        visibility: 'off',
      },
    ],
  },
  {
    featureType: 'poi.business',
    stylers: [
      {
        visibility: 'off',
      },
    ],
  },
  {
    featureType: 'road',
    stylers: [
      {
        saturation: 5,
      },
      {
        lightness: 35,
      },
    ],
  },
  {
    featureType: 'road',
    elementType: 'labels.icon',
    stylers: [
      {
        visibility: 'off',
      },
    ],
  },
  {
    featureType: 'road.local',
    elementType: 'labels',
    stylers: [
      {
        visibility: 'off',
      },
    ],
  },
  {
    featureType: 'transit',
    stylers: [
      {
        visibility: 'off',
      },
    ],
  },
  {
    featureType: 'water',
    stylers: [
      {
        saturation: 30,
      },
      {
        lightness: -20,
      },
    ],
  },
]

var app = {
  init: function () {
    var _this = this
    _this.docHeight()
    _this.initHeader()
    _this.initPageNavigation()
    _this.initPostTeaser()
    _this.initLatestNews()
    _this.initProjectRelated()
    _this.initDrawers()
    _this.initBankSearch()
    _this.initHomeBanche()
    _this.initHomePartners()
    _this.initScrollLink()
    _this.enableClipboardText()
    _this.initHomeProgettualita()
    _this.initAccordions()
    _this.initTimeline()
    _this.isTouchable()
    _this.initGrid()
    _this.enableGalleryBlock()
    _this.initGlossaryNav()
    _this.initInternalMenu()
    _this.initFacetFix()

    Fancybox.bind('[data-fancybox]', {
      autoFocus: false,
      Thumbs: false,
      Toolbar: {
        display: {
          left: [],
          middle: [],
          right: ['close'],
        },
      },
    })
    if (jQuery('#banks-map').length > 0) {
      // L'inizializzazione della mappa avviene nel callback globale fedlusInitMap
    }
    if (jQuery('#single-bank-map').length > 0) {
      // L'inizializzazione della mappa singola avviene nel callback globale fedlusInitMap
    }
  },
  initTimeline: function () {
    var waypoints = jQuery('.timeline-entry .timeline-year').waypoint(
      function (direction) {
        const el = jQuery(this.element).closest('.timeline-entry')
        if (direction == 'down') {
          el.addClass('active')
        } else {
          if (el.prev('.timeline-entry').length) {
            el.removeClass('active')
          }
        }
      },
      {
        offset: function () {
          return Waypoint.viewportHeight() / 2 - this.element.clientHeight / 2
        },
      },
    )
  },
  initGlossaryNav: function () {
    const nav = document.querySelector('.glossary-nav')
    if (!nav) return

    const links = Array.from(
      nav.querySelectorAll('.glossary-nav-link[href^="#"]'),
    )

    // Click con offset dedicato al glossario
    nav.addEventListener('click', function (event) {
      const link = event.target.closest('.glossary-nav-link')
      if (!link || !link.hash) return

      const target = document.querySelector(link.hash)
      if (!target) return

      event.preventDefault()

      const rect = target.getBoundingClientRect()
      const scrollTop =
        window.pageYOffset || document.documentElement.scrollTop || 0
      const headerOffset = 140
      const top = rect.top + scrollTop - headerOffset

      window.scrollTo({
        top,
        behavior: 'smooth',
      })
    })

    // Evidenziazione lettera attiva durante lo scroll (se Waypoints è disponibile)
    if (typeof Waypoint !== 'undefined' && links.length) {
      const linkById = {}
      links.forEach((link) => {
        const hash = link.getAttribute('href') || link.hash
        if (!hash || hash.charAt(0) !== '#') return
        const id = hash.substring(1)
        linkById[id] = link
      })

      Object.keys(linkById).forEach((id) => {
        const section = document.getElementById(id)
        if (!section) return

        // Usa l'elemento header della lettera se presente, altrimenti la section
        const targetEl =
          section.querySelector('.glossary-letter-title') || section

        new Waypoint({
          element: targetEl,
          handler: function (direction) {
            // Attiva su scroll in giù quando la lettera entra a metà viewport
            if (direction === 'down') {
              const activeLink = linkById[id]
              if (!activeLink) return
              links.forEach((l) => l.classList.remove('active'))
              activeLink.classList.add('active')
            }
          },
          offset: '50%',
        })

        // Waypoint inverso per scroll verso l'alto
        new Waypoint({
          element: targetEl,
          handler: function (direction) {
            if (direction === 'up') {
              const activeLink = linkById[id]
              if (!activeLink) return
              links.forEach((l) => l.classList.remove('active'))
              activeLink.classList.add('active')
            }
          },
          offset: 0,
        })
      })
    }
  },
  initInternalMenu: function () {
    // Controlla solo su schermi mobili
    if (!this.isMobile()) {
      return
    }

    const menuList = document.querySelector('.menu-internal ul')
    if (!menuList) return

    const activeItem = menuList.querySelector('li.current-menu-item')
    if (!activeItem) return

    // Diamo un piccolo ritardo per assicurarci che il layout sia stabile al caricamento della pagina
    setTimeout(() => {
      activeItem.scrollIntoView({
        behavior: 'auto', // 'auto' è meglio di 'smooth' al caricamento della pagina
        inline: 'center',
        block: 'nearest',
      })
    }, 150)
  },
  initAccordions: function () {
    jQuery('.enrichment-accordion .title .handle').on(
      'click keydown',
      function (e) {
        // Handle keyboard events
        if (e.type === 'keydown') {
          // Only handle Enter (13) and Space (32)
          if (e.which !== 13 && e.which !== 32) {
            return
          }
          e.preventDefault()
        }

        const button = jQuery(this)
        const item = button.closest('.enrichment-accordion')
        const content = item.find('.content')
        const isOpen = item.hasClass('open')
        const contentId = button.attr('aria-controls')

        // Toggle content
        content.slideToggle(300)
        item.toggleClass('open')

        // Update ARIA attributes
        button.attr('aria-expanded', !isOpen)
        content.attr('aria-hidden', isOpen)

        // Announce to screen readers
        if (typeof A11yUtils !== 'undefined') {
          const buttonText = button.text().replace(/\s+/g, ' ').trim()
          A11yUtils.LiveAnnouncer.announce(
            isOpen ? `${buttonText} chiuso` : `${buttonText} aperto`,
          )
        }
      },
    )
  },
  enableClipboardText: function () {
    jQuery('.to-clipboard').on('click', function () {
      const text = jQuery(this).data('text')
      const button = jQuery(this)
      let label = 'Link copiato negli appunti'
      const feedback = jQuery('<span />', {
        html: label,
        class: 'feedback',
      })
        .appendTo(button)
        .fadeIn()
      navigator.clipboard.writeText(text).then(() => {
        setTimeout(() => {
          feedback.fadeOut(300, () => feedback.remove())
        }, 2000)
      })
      return false
    })
  },
  initHeader: function () {
    const _this = this
    _this.scrollPosition = 0

    // Initialize Focus Traps for mobile menu and panel
    let menuFocusTrap = null
    let mobilePanelFocusTrap = null

    // Create focus trap for main mobile menu
    if (typeof A11yUtils !== 'undefined') {
      menuFocusTrap = A11yUtils.FocusTrap.create('#header-content', {
        escapeDeactivates: true,
        onDeactivate: function () {
          jQuery('body').removeClass('menu-open')
          jQuery('#header .menu-handle').attr('aria-expanded', 'false')
        },
      })

      // Create focus trap for mobile panel
      mobilePanelFocusTrap = A11yUtils.FocusTrap.create('#mobile-panel', {
        escapeDeactivates: true,
        initialFocus: '.back',
        onDeactivate: function () {
          jQuery('#mobile-panel').removeClass('show')
          jQuery('#header .top ul button[aria-expanded="true"]').attr(
            'aria-expanded',
            'false',
          )
        },
      })
    }

    jQuery(window).on('scroll', function () {
      const pos = jQuery(this).scrollTop()
      if (pos < _this.scrollPosition) {
        jQuery('html').addClass('scrolling-top')
      } else {
        jQuery('html').removeClass('scrolling-top')
      }
      if (pos > 100) {
        jQuery('html').addClass('is-scrolled')
      } else {
        jQuery('html').removeClass('is-scrolled')
      }
      _this.scrollPosition = pos
    })

    // Handle mobile panel opening
    jQuery('#header .top ul button').on('click', function () {
      if (!_this.isLaptop()) {
        const title = jQuery(this).data('title')
        const button = jQuery(this)
        const ul = button.closest('li').find('ul').clone()

        jQuery('#mobile-panel .title').text(title)
        jQuery('#mobile-panel .submenu').html(ul)
        jQuery('#mobile-panel').addClass('show').attr('aria-hidden', 'false')

        // Update aria-expanded
        button.attr('aria-expanded', 'true')

        // Announce to screen readers
        if (typeof A11yUtils !== 'undefined') {
          A11yUtils.LiveAnnouncer.announce(`Pannello ${title} aperto`)

          // Activate focus trap
          if (mobilePanelFocusTrap) {
            mobilePanelFocusTrap.activate()
          }
        }
      }
    })

    // Handle main menu toggle
    jQuery('#header .menu-handle').on('click', function () {
      const isOpen = jQuery('body').hasClass('menu-open')
      const button = jQuery(this)

      jQuery('body').toggleClass('menu-open')

      // Update ARIA attributes
      button.attr('aria-expanded', !isOpen)
      button.attr(
        'aria-label',
        isOpen ? 'Apri menu di navigazione' : 'Chiudi menu di navigazione',
      )
      jQuery('#header-content').attr('aria-hidden', isOpen)

      // Announce to screen readers
      if (typeof A11yUtils !== 'undefined') {
        A11yUtils.LiveAnnouncer.announce(isOpen ? 'Menu chiuso' : 'Menu aperto')

        // Manage focus trap
        if (menuFocusTrap) {
          if (!isOpen) {
            menuFocusTrap.activate()
          } else {
            menuFocusTrap.deactivate()
          }
        }
      }
    })

    // Handle submenu toggle in mobile menu with full keyboard support
    jQuery('#header .menu > li > a.chevroned').on(
      'click keydown',
      function (e) {
        // Handle keyboard events
        if (e.type === 'keydown') {
          // Only handle Enter (13) and Space (32)
          if (e.which !== 13 && e.which !== 32) {
            return
          }
          e.preventDefault()
        }

        if (!_this.isLaptop()) {
          const link = jQuery(this)
          const item = link.closest('li')
          const submenu = item.find('ul').first()
          const isOpen = item.hasClass('open')
          const submenuId = link.attr('aria-controls')

          // Toggle submenu
          submenu.slideToggle(300, function () {
            // Add/remove ID for aria-controls relationship
            if (!isOpen && submenuId) {
              submenu.attr('id', submenuId)
              submenu.attr('aria-hidden', 'false')
            } else if (isOpen && submenuId) {
              submenu.removeAttr('id')
              submenu.attr('aria-hidden', 'true')
            }
          })

          item.toggleClass('open')

          // Update aria-expanded
          link.attr('aria-expanded', !isOpen)

          // Announce to screen readers
          if (typeof A11yUtils !== 'undefined') {
            const linkText = link.text().replace(/\s+/g, ' ').trim()
            A11yUtils.LiveAnnouncer.announce(
              isOpen
                ? `Sottomenu ${linkText} chiuso`
                : `Sottomenu ${linkText} aperto`,
            )
          }

          return false
        }
      },
    )

    // Close submenus with Escape key
    jQuery(document).on('keydown', function (e) {
      if (e.key === 'Escape' && !_this.isLaptop()) {
        const openSubmenu = jQuery('#header .menu > li.open')
        if (openSubmenu.length > 0) {
          const link = openSubmenu.find('> a.chevroned')
          const submenu = openSubmenu.find('ul').first()

          submenu.slideUp(300, function () {
            submenu.removeAttr('id')
            submenu.attr('aria-hidden', 'true')
          })

          openSubmenu.removeClass('open')
          link.attr('aria-expanded', 'false')

          // Return focus to the trigger
          link.focus()

          // Announce closure
          if (typeof A11yUtils !== 'undefined') {
            const linkText = link.text().replace(/\s+/g, ' ').trim()
            A11yUtils.LiveAnnouncer.announce(`Sottomenu ${linkText} chiuso`)
          }
        }
      }
    })

    // Handle mobile panel back button
    jQuery('#mobile-panel .back').on('click', function () {
      jQuery('#mobile-panel').removeClass('show').attr('aria-hidden', 'true')
      jQuery('#header .top ul button[aria-expanded="true"]').attr(
        'aria-expanded',
        'false',
      )

      // Announce and deactivate focus trap
      if (typeof A11yUtils !== 'undefined') {
        A11yUtils.LiveAnnouncer.announce('Tornato al menu principale')

        if (mobilePanelFocusTrap) {
          mobilePanelFocusTrap.deactivate()
        }
      }

      return false
    })

    // Handle mobile panel close button
    jQuery('#mobile-panel .close').on('click', function () {
      jQuery('#mobile-panel').removeClass('show').attr('aria-hidden', 'true')
      jQuery('#header .top ul button[aria-expanded="true"]').attr(
        'aria-expanded',
        'false',
      )

      // Announce and deactivate focus traps
      if (typeof A11yUtils !== 'undefined') {
        A11yUtils.LiveAnnouncer.announce('Pannello chiuso')

        if (mobilePanelFocusTrap) {
          mobilePanelFocusTrap.deactivate()
        }
      }

      setTimeout(function () {
        jQuery('body').removeClass('menu-open')
        jQuery('#header .menu-handle')
          .attr('aria-expanded', 'false')
          .attr('aria-label', 'Apri menu di navigazione')
        jQuery('#header-content').attr('aria-hidden', 'true')

        if (menuFocusTrap) {
          menuFocusTrap.deactivate()
        }
      }, 100)

      return false
    })
    //jQuery("#header-logo").clone().appendTo("#header-clone-wrap");
    //jQuery("#header-main-menu").clone().appendTo("#header-clone-wrap");
  },
  initPageNavigation: function () {
    jQuery('.page-navigation').each(function () {
      const item = jQuery(this).find('.current a')
      const wrap = jQuery(this)

      ////////////////////////////////// Ore buttate su questa cosa: 3

      //const childOffsetLeft2 = item.offsetLeft + item.offsetWidth;
      //const containerScrollLeft2 = wrap.scrollLeft + wrap.offsetWidth;

      //if (wrap.scrollLeft > item.offsetLeft) {
      //  wrap.scrollLeft = item.offsetLeft;
      //}

      //if (containerScrollLeft2 < childOffsetLeft2) {
      //  wrap.scrollLeft += childOffsetLeft2 - containerScrollLeft2;
      //}

      //const offset = item.offset().left; //+ wrap.scrollLeft(); // - window.innerWidth / 2 + wrap.outerWidth() / 2
      //const offset = window.innerWidth / 2 + item.width();
      //const element = jQuery(this)[0]
      ////element.scrollTo({ left: offset, behavior: 'smooth' })
      //element.scrollLeft(0);
      //wrap.scrollLeft(item.offset().left);
      //wrap.scrollLeft(item.position().left + item.width())
      //$(this).find(".current a")[0].scrollIntoView({inline:'center'});
    })
  },
  initPostTeaser: function () {
    jQuery('.post-teaser .post-image, .post-teaser .post-link').hover(
      function () {
        console.log('OK')
        jQuery(this).closest('.post-teaser').addClass('hover')
      },
      function () {
        jQuery(this).closest('.post-teaser').removeClass('hover')
      },
    )
  },
  isDesktop: () => window.matchMedia('(min-width: 1280px)').matches,
  isLaptop: () => window.matchMedia('(min-width: 1124px)').matches,
  isMobile: () => window.matchMedia('(max-width: 767px)').matches,

  isTouchable() {
    if (!window.matchMedia('(pointer: coarse)').matches) {
      document.documentElement.classList.add('not-touch')
    }
  },
  initGrid: function () {
    const heightOutput = document.querySelector('#debug-height')
    const widthOutput = document.querySelector('#debug-width')
    const ContainerWidthOutput = document.querySelector('#debug-container')
    const ContainerClass = document.querySelector('.container')

    // Verifica che gli elementi di debug esistano prima di usarli
    if (heightOutput && widthOutput && ContainerWidthOutput && ContainerClass) {
      function reportWindowSize() {
        heightOutput.textContent = window.innerHeight
        widthOutput.textContent = window.innerWidth
        ContainerWidthOutput.textContent = parseFloat(
          window.getComputedStyle(ContainerClass).width,
        )
      }
      window.addEventListener('resize', reportWindowSize)
      reportWindowSize()
    }

    document.addEventListener('keydown', (event) => {
      if (
        event.ctrlKey &&
        String.fromCharCode(event.which).toLowerCase() === 'g'
      ) {
        const gridEl = document.querySelector('#grid')
        const sizeEl = document.querySelector('#size')
        if (gridEl) gridEl.classList.toggle('show')
        if (sizeEl) sizeEl.classList.remove('show')
      }
      if (
        event.ctrlKey &&
        String.fromCharCode(event.which).toLowerCase() === 's'
      ) {
        const sizeEl = document.querySelector('#size')
        if (sizeEl) sizeEl.classList.toggle('show')
      }
    })
  },
  initLatestNews: function () {
    const swiper = new Swiper('#last-updates .slider', {
      wrapperClass: 'slider-wrapper',
      slideClass: 'slide',
      slidesPerView: 'auto',
      pagination: {
        el: '#last-updates .slider-pager',
      },
    })
  },
  initProjectRelated: function () {
    const swiper = new Swiper('#project-related .slider', {
      wrapperClass: 'slider-wrapper',
      slideClass: 'slide',
      slidesPerView: 'auto',
      navigation: {
        nextEl: '#project-related .arrows .next',
        prevEl: '#project-related .arrows .prev',
      },
      pagination: {
        el: '#project-related .slider-pager',
      },
    })
  },
  initDrawers: function () {
    // Store focus traps for each drawer (inclusi i filtri FacetWP)
    const drawerFocusTraps = new Map()

    // Store the trigger element to return focus when drawer closes
    let lastDrawerTrigger = null

    // Utility per disabilitare/ripristinare i focusable dentro un drawer quando è chiuso/aperto
    function setDrawerFocusables(drawerEl, enabled) {
      const container = drawerEl.querySelector('.drawer-content') || drawerEl
      if (!container) return

      const focusableSelectors = [
        'a[href]',
        'button',
        'textarea',
        'input:not([type="hidden"])',
        'select',
        '[tabindex]',
      ].join(', ')

      container.querySelectorAll(focusableSelectors).forEach((el) => {
        if (enabled) {
          if (el.__fedlusPrevTabindex !== undefined) {
            if (el.__fedlusPrevTabindex === null) {
              el.removeAttribute('tabindex')
            } else {
              el.setAttribute('tabindex', el.__fedlusPrevTabindex)
            }
          } else if (el.getAttribute('tabindex') === '-1') {
            el.removeAttribute('tabindex')
          }
        } else {
          if (el.__fedlusPrevTabindex === undefined) {
            const prev = el.getAttribute('tabindex')
            el.__fedlusPrevTabindex = prev !== null ? prev : null
          }
          el.setAttribute('tabindex', '-1')
        }
      })
    }

    // Create focus trap for each drawer
    if (typeof A11yUtils !== 'undefined') {
      jQuery('.drawer').each(function () {
        const drawer = this
        const drawerId = jQuery(drawer).attr('id')

        // All'inizio: drawer chiuso, togli TUTTI i focusable dall'ordine di tab (compreso .drawer-close)
        setDrawerFocusables(drawer, false)
        const closeBtn = drawer.querySelector('.drawer-close')
        if (closeBtn) {
          if (closeBtn.__fedlusPrevTabindex === undefined) {
            const prev = closeBtn.getAttribute('tabindex')
            closeBtn.__fedlusPrevTabindex = prev !== null ? prev : null
          }
          closeBtn.setAttribute('tabindex', '-1')
        }

        // Trova l'elemento per il focus iniziale
        const initialFocusEl =
          drawer.querySelector('.drawer-close') ||
          drawer.querySelector('button') ||
          drawer.querySelector('a')

        const focusTrap = A11yUtils.FocusTrap.create(drawer, {
          escapeDeactivates: true,
          initialFocus: initialFocusEl,
          onActivate: function () {
            setDrawerFocusables(drawer, true)
            const closeBtn = drawer.querySelector('.drawer-close')
            if (closeBtn) {
              if (closeBtn.__fedlusPrevTabindex !== undefined) {
                if (closeBtn.__fedlusPrevTabindex === null) {
                  closeBtn.removeAttribute('tabindex')
                } else {
                  closeBtn.setAttribute(
                    'tabindex',
                    closeBtn.__fedlusPrevTabindex,
                  )
                }
              } else {
                closeBtn.removeAttribute('tabindex')
              }
            }
          },
          onDeactivate: function () {
            setDrawerFocusables(drawer, false)
            const closeBtn = drawer.querySelector('.drawer-close')
            if (closeBtn) {
              if (closeBtn.__fedlusPrevTabindex === undefined) {
                const prev = closeBtn.getAttribute('tabindex')
                closeBtn.__fedlusPrevTabindex = prev !== null ? prev : null
              }
              closeBtn.setAttribute('tabindex', '-1')
            }
            jQuery(drawer).removeClass('show')
            jQuery('html').removeClass('drawered')

            // Return focus to trigger element
            setTimeout(() => {
              try {
                const trigger = lastDrawerTrigger
                if (
                  trigger &&
                  typeof trigger.focus === 'function' &&
                  document.contains(trigger)
                ) {
                  trigger.focus()
                }
              } catch (e) {
                // ignora errori di focus su trigger non più nel DOM
              }
            }, 50)
            lastDrawerTrigger = null
          },
        })

        if (drawerId) {
          drawerFocusTraps.set(drawerId, focusTrap)
        }
      })
    }

    // Apertura drawer (funziona sia per drawer grandi che per drawer filtri)
    jQuery('.drawer-handle').on('click', function () {
      const trigger = this
      const el = jQuery(jQuery(this).attr('href'))
      const drawerId = el.attr('id')

      el.addClass('show').attr('aria-hidden', 'false')
      jQuery('html').addClass('drawered')

      // Store trigger for focus return (solo se esiste un focus trap per questo drawer)
      if (drawerId && drawerFocusTraps.has(drawerId)) {
        lastDrawerTrigger = trigger

        // Activate focus trap
        const focusTrap = drawerFocusTraps.get(drawerId)
        setTimeout(() => {
          focusTrap.activate()
        }, 50)
      }

      // Announce to screen readers
      if (typeof A11yUtils !== 'undefined') {
        const title = el.find('.person-title').first().text() || 'Dettaglio'
        A11yUtils.LiveAnnouncer.announce(`${title} aperto`)
      }

      return false
    })

    // Chiusura da pulsante X
    jQuery('.drawer-close').on('click', function () {
      const drawer = jQuery(this).closest('.drawer')
      const drawerId = drawer.attr('id')

      drawer.removeClass('show').attr('aria-hidden', 'true')
      jQuery('html').removeClass('drawered')

      // Deactivate focus trap solo per drawer con trap
      if (drawerId && drawerFocusTraps.has(drawerId)) {
        const focusTrap = drawerFocusTraps.get(drawerId)
        focusTrap.deactivate()
      }

      // Nessun uso diretto di lastDrawerTrigger qui: ci pensa onDeactivate
      if (typeof A11yUtils !== 'undefined') {
        const title = drawer.find('.person-title').first().text() || 'Dettaglio'
        A11yUtils.LiveAnnouncer.announce(`${title} chiuso`)
      }
    })

    // Chiusura cliccando fuori (overlay)
    jQuery('#drawer-overlay').on('click', function () {
      jQuery('.drawer.show').each(function () {
        const drawer = jQuery(this)
        const drawerId = drawer.attr('id')

        drawer.removeClass('show').attr('aria-hidden', 'true')

        // Deactivate focus trap solo per drawer con trap
        if (drawerId && drawerFocusTraps.has(drawerId)) {
          const focusTrap = drawerFocusTraps.get(drawerId)
          focusTrap.deactivate()
        }
      })

      jQuery('html').removeClass('drawered')
    })

    // Chiusura con ESC
    jQuery(document).on('keydown', function (event) {
      if (event.key == 'Escape') {
        jQuery('.drawer.show').each(function () {
          const drawer = jQuery(this)
          const drawerId = drawer.attr('id')

          drawer.removeClass('show').attr('aria-hidden', 'true')

          // Deactivate focus trap solo per drawer con trap
          if (drawerId && drawerFocusTraps.has(drawerId)) {
            const focusTrap = drawerFocusTraps.get(drawerId)
            focusTrap.deactivate()
          }
        })

        jQuery('html').removeClass('drawered')
      }
    })

    // Gestione centralizzata dei filtri FacetWP nei drawer (multi-drawer)
    const sortFacetName = 'sort_'

    function getAppliedFiltersCount() {
      if (typeof FWP === 'undefined' || !FWP.facets) {
        return 0
      }

      let count = 0
      for (const facet in FWP.facets) {
        if (
          facet !== sortFacetName &&
          Array.isArray(FWP.facets[facet]) &&
          FWP.facets[facet].length > 0
        ) {
          count += FWP.facets[facet].length
        }
      }
      return count
    }

    function updateFilterLabels() {
      const appliedCount = getAppliedFiltersCount()
      const handles = document.querySelectorAll('.drawer-handle')

      handles.forEach((handle) => {
        const label = handle.querySelector('.filter-label')
        if (!label) return

        if (appliedCount > 0) {
          label.textContent = 'Filtri'
          handle.classList.add('has-filters')
        } else {
          label.textContent = 'Applica filtri'
          handle.classList.remove('has-filters')
        }
      })
    }

    function closeFilterDrawers() {
      const html = document.documentElement
      html.classList.remove('drawered')

      const drawerOverlay = document.getElementById('drawer-overlay')
      if (drawerOverlay) {
        drawerOverlay.classList.remove('show')
      }

      const openDrawers = document.querySelectorAll('.drawer.show')
      openDrawers.forEach((drawerEl) => {
        const drawer = jQuery(drawerEl)
        const drawerId = drawer.attr('id')

        drawer.removeClass('show').attr('aria-hidden', 'true')

        if (drawerId && drawerFocusTraps.has(drawerId)) {
          const focusTrap = drawerFocusTraps.get(drawerId)
          focusTrap.deactivate()
        }
      })
    }

    function applyFilters() {
      const applyButtons = document.querySelectorAll('.button-apply-filters')
      applyButtons.forEach((btn) => {
        btn.setAttribute('disabled', 'disabled')
        btn.classList.add('loading')
      })

      if (typeof FWP !== 'undefined') {
        if (typeof FWP.toggleOverlay === 'function') {
          FWP.toggleOverlay('on')
        }
        FWP.fetchData()
        if (typeof FWP.setHash === 'function') {
          FWP.setHash()
        }
      }

      closeFilterDrawers()
    }

    function resetFilters() {
      if (typeof FWP !== 'undefined') {
        FWP.reset()
      }
    }

    // Click sui bottoni di applicazione/reset (tutti i drawer)
    document.querySelectorAll('.button-apply-filters').forEach((btn) => {
      btn.addEventListener('click', function (e) {
        e.preventDefault()
        applyFilters()
      })
    })

    document.querySelectorAll('.button-reset-filters').forEach((btn) => {
      btn.addEventListener('click', function (e) {
        e.preventDefault()
        resetFilters()
      })
    })

    // Gestione sort-radio globale
    document.addEventListener('click', function (e) {
      const target = e.target
      if (!(target instanceof Element)) return

      if (target.classList.contains('sort-radio')) {
        const val = target.getAttribute('data-value')
        if (typeof FWP !== 'undefined') {
          FWP.facets[sortFacetName] = [val]
          applyFilters()
        }
      }
    })

    // Aggiorna label, sort, stato bottoni e accessibilità facet dopo ogni caricamento FacetWP
    document.addEventListener('facetwp-loaded', function () {
      updateFilterLabels()

      // Reset stato pulsanti "Applica filtri"
      document.querySelectorAll('.button-apply-filters').forEach((btn) => {
        btn.removeAttribute('disabled')
        btn.classList.remove('loading')
      })

      // Aggiornamento sort-radio in base alla faccetta di sort
      if (
        typeof FWP !== 'undefined' &&
        typeof FWP.facets[sortFacetName] !== 'undefined'
      ) {
        const activeValue = FWP.facets[sortFacetName][0] || ''
        const sortRadios = document.querySelectorAll('.sort-radio')
        sortRadios.forEach((radio) => {
          if (radio.getAttribute('data-value') === activeValue) {
            radio.classList.add('checked')
          } else {
            radio.classList.remove('checked')
          }
        })
      }

      // Rende navigabili da tastiera tutti i facetwp-checkbox
      document.querySelectorAll('.facetwp-checkbox').forEach((checkboxEl) => {
        // Imposta focusabilità e ruolo
        checkboxEl.setAttribute('tabindex', '0')
        checkboxEl.setAttribute('role', 'checkbox')

        // Stato aria-checked basato sulla classe FacetWP
        const isChecked = checkboxEl.classList.contains('checked')
        checkboxEl.setAttribute('aria-checked', isChecked ? 'true' : 'false')

        // Evita di aggiungere più listener allo stesso elemento
        if (checkboxEl.__fedlusKbHandled) return
        checkboxEl.__fedlusKbHandled = true

        checkboxEl.addEventListener('keydown', function (e) {
          if (e.key === ' ' || e.key === 'Spacebar' || e.key === 'Enter') {
            e.preventDefault()
            checkboxEl.click()
          }
        })
      })

      // Dopo che FacetWP ha (ri)generato il markup,
      // assicuriamoci che i drawer chiusi restino fuori dal tab order
      document.querySelectorAll('.drawer').forEach((drawerEl) => {
        if (!drawerEl.classList.contains('show')) {
          setDrawerFocusables(drawerEl, false)
        }
      })
    })

    // Inizializza label filtri appena possibile
    let facetInitTries = 0
    ;(function initFacetLabels() {
      if (typeof FWP !== 'undefined' && FWP.response) {
        updateFilterLabels()
      } else if (facetInitTries < 10) {
        facetInitTries++
        setTimeout(initFacetLabels, 100)
      }
    })()

    let facetInitDone = false

    document.addEventListener('facetwp-loaded', function () {
      if (facetInitDone) return
      facetInitDone = true
      document.querySelectorAll('.drawer').forEach((drawerEl) => {
        if (!drawerEl.classList.contains('show')) {
          setDrawerFocusables(drawerEl, false)
        }
      })
    })
  },
  initBankSearch: function () {
    const searchTitle = jQuery('#banks-toolbar .title')
    const defaultTitle = searchTitle.text()
    jQuery('#banks-toolbar input').on('input', function () {
      var val = jQuery(this).val()
      if (!val) {
        jQuery('#banks-list li').show()
        jQuery('#banks-list-empty').hide()
        return
      }
      var keywords = val.split(' ')
      var has_results = false
      jQuery('#banks-list li').each(function () {
        var bank = jQuery(this)
        var name =
          jQuery(this).find('.name').text() + jQuery(this).find('p').text()
        var match = true
        name = name.toLowerCase()
        keywords.forEach(function (keyword) {
          if (name.indexOf(keyword.toLowerCase()) == -1) {
            match = false
          } else {
            has_results = true
          }
        })
        if (match) {
          bank.show().addClass('match')
        } else {
          bank.hide().removeClass('match')
        }
        var num = jQuery('#banks-list li.match').length
        const findTitle =
          num == 0
            ? 'Nessun risultato'
            : num == 1
            ? num + ' Banca trovata'
            : num + ' Banche trovate'
        searchTitle.html(findTitle)
      })
      if (!has_results) {
        jQuery('#banks-list-empty').show()
      } else {
        jQuery('#banks-list-empty').hide()
      }
    })
    jQuery('#banks-toolbar input').keyup(function () {
      var input = jQuery.trim(jQuery(this).val())
      if (input == '') {
        searchTitle.text(defaultTitle)
      }
    })
  },
  initHomeBanche: function () {
    const swiper = new Swiper('.banks-marquee2 .swiper', {
      spaceBetween: 0,
      centeredSlides: true,
      speed: 6000,
      autoplay: {
        delay: 1,
        disableOnInteraction: true,
      },
      loop: true,
      slidesPerView: 'auto',
      allowTouchMove: false,
    })
  },
  initHomePartners: function () {
    const swiper = new Swiper('#home-partners .slider', {
      wrapperClass: 'slider-wrapper',
      slideClass: 'slide',
      spaceBetween: 0,
      centeredSlides: true,
      speed: 6000,
      autoplay: {
        delay: 1,
        disableOnInteraction: false,
      },
      loop: true,
      slidesPerView: 'auto',
      allowTouchMove: false,
    })
  },
  initHomeProgettualita: function () {
    const swiper = new Swiper('#home-progettualita .slider', {
      wrapperClass: 'slider-wrapper',
      slideClass: 'slide',
      spaceBetween: 0,
      centeredSlides: true,
      autoplay: {
        delay: 3000,
      },
      loop: true,
      slidesPerView: 'auto',
      pagination: {
        el: '#home-progettualita .slider-pager',
      },
    })
  },
  initScrollLink: function () {
    jQuery('.scroll-link').on('click', function () {
      const el = jQuery(jQuery(this).attr('href'))
      const offset = el.offset().top
      jQuery('html, body').animate({ scrollTop: offset - 40 })
      return false
    })
  },
  createSingleBankMap: function () {
    const mapElement = document.getElementById('single-bank-map')
    if (!mapElement) return

    const lat = parseFloat(mapElement.getAttribute('data-lat'))
    const lng = parseFloat(mapElement.getAttribute('data-lng'))
    const bankName = mapElement.getAttribute('data-bank-name')
    const address = mapElement.getAttribute('data-address')
    const city = mapElement.getAttribute('data-city')
    const region = mapElement.getAttribute('data-region')
    const url = mapElement.getAttribute('data-url')
    const markerImage = mapElement.getAttribute('data-marker')

    if (!lat || !lng) return

    // Usa gli stessi stili della mappa principale
    const mapStyle = FEDLUS_MAP_STYLE

    // Crea la mappa
    const map = new google.maps.Map(mapElement, {
      center: { lat: lat, lng: lng },
      zoom: 15,
      disableDefaultUI: true,
      zoomControl: true,
      zoomControlOptions: {
        position: google.maps.ControlPosition.RIGHT_BOTTOM,
      },
      styles: mapStyle,
    })

    // Crea il marker
    const marker = new google.maps.Marker({
      position: { lat: lat, lng: lng },
      map: map,
      icon: markerImage,
      title: bankName,
    })

    // Costruisci il contenuto del popover
    let popoverContent = `<div class="map-popover">`
    popoverContent += `<div class="map-popover-title">${bankName}</div>`

    // Indirizzo completo
    if (address || city || region) {
      let fullAddress = ''
      if (address) fullAddress += address
      if (city) {
        if (fullAddress) fullAddress += '<br>'
        fullAddress += city
      }
      // if (region) {
      //   if (fullAddress) fullAddress += ' — '
      //   fullAddress += region
      // }
      if (fullAddress) {
        popoverContent += `<div class="map-popover-address">${fullAddress}</div>`
      }
    }

    // Link al sito
    if (url) {
      popoverContent += `<div class="map-popover-link"><a href="${url}" target="_blank" rel="noopener">Vai al sito web →</a></div>`
    }

    popoverContent += `</div>` // Info window con contenuto completo
    const infoWindow = new google.maps.InfoWindow({
      content: popoverContent,
    })

    // Apri info window al click
    marker.addListener('click', () => {
      infoWindow.open(map, marker)
    })
  },
  createMap: function () {
    var _this = this
    let suppressMapFocusRouting = false
    const suppressFocus = (ms = 600) => {
      suppressMapFocusRouting = true
      setTimeout(() => {
        suppressMapFocusRouting = false
      }, ms)
    }
    const mapStyle = FEDLUS_MAP_STYLE
    const markerImage = jQuery('#banks-map').data('marker')
    const markerOpenImage = jQuery('#banks-map').data('marker-open')
    const markerClusterImage = jQuery('#banks-map').data('marker-cluster')
    const rawMapId = jQuery('#banks-map').data('map-id')
    const mapId =
      rawMapId && String(rawMapId).trim() !== '' ? rawMapId : 'DEMO_MAP_ID'
    // Disabilitiamo temporaneamente Advanced Markers per permettere stili personalizzati
    const useAdvancedMarkers = true // !!mapId
    if (mapId === 'DEMO_MAP_ID') {
      console.info(
        'Usando DEMO_MAP_ID: Advanced Marker abilitati senza Map ID personalizzato. Definisci GMAPS_MAP_ID per usare il tuo ID mappa.',
      )
    }
    var map = new google.maps.Map(document.getElementById('banks-map'), {
      disableDefaultUI: true,
      zoomControl: true,
      zoomControlOptions: {
        position: google.maps.ControlPosition.LEFT_BOTTOM,
      },
      // Evita che la mappa intercetti scorciatoie tastiera (freccie/+) che possono interferire con il focus
      keyboardShortcuts: false,
      styles: mapStyle,
      ...(useAdvancedMarkers ? { mapId } : {}),
    })
    // Considera solo gli elementi con coordinate valide
    var els = jQuery('#banks-list li[data-lat][data-lng]')
    const bounds = new google.maps.LatLngBounds()
    // Stato leggero per gestione chiusura/annunci
    let lastInfoWindow = null
    let lastMarker = null
    let lastImg = null
    let lastIsAdvanced = false
    let lastTriggerEl = null
    let nextTriggerEl = null
    var markers = els.map(function () {
      const el = this
      const position = {
        lat: parseFloat(jQuery(el).data('lat')),
        lng: parseFloat(jQuery(el).data('lng')),
      }
      var myLatLng = new google.maps.LatLng(position)
      let marker
      let img
      const bankName = jQuery(el)
        .find('.name')
        .text()
        .replace(/\s+/g, ' ')
        .trim()
      // Crea contenuto come nodo focusabile per gestione focus
      const contentEl = document.createElement('div')
      contentEl.className = 'popover'
      contentEl.setAttribute('tabindex', '-1')
      contentEl.innerHTML = jQuery(el).html()
      if (useAdvancedMarkers) {
        // Advanced Marker with image content
        img = document.createElement('img')
        img.src = markerImage
        img.alt = ''
        img.setAttribute('aria-hidden', 'true')
        // Accessibilità: marker non focusabile direttamente (limitazione AdvancedMarkerElement)
        img.className = 'map-adv-marker'
        // Etichetta solo per annunci (non per focus)
        img.setAttribute(
          'data-a11y-label',
          bankName ? `Apri dettagli: ${bankName}` : 'Apri dettagli banca',
        )
        marker = new google.maps.marker.AdvancedMarkerElement({
          position: myLatLng,
          content: img,
          gmpClickable: true,
        })
        // Collega l'istanza al nodo per gestione tastiera dal container
        img.__am = marker
      } else {
        // Fallback: classic Marker without Advanced features
        marker = new google.maps.Marker({
          position: myLatLng,
          icon: markerImage,
        })
      }
      bounds.extend(myLatLng)
      var content = '<div class="popover">' + jQuery(el).html() + '</div>'
      var infowindow = new google.maps.InfoWindow()
      // Quando il DOM dell'InfoWindow è pronto, porta il focus dentro il popover
      function focusFirstInPopover() {
        try {
          const firstFocusable = contentEl.querySelector(
            'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), [tabindex]:not([tabindex="-1"])',
          )
          if (firstFocusable && typeof firstFocusable.focus === 'function') {
            firstFocusable.focus()
          } else {
            contentEl.focus()
          }
        } catch (e) {}
      }
      infowindow.addListener('domready', function () {
        // piccola attesa per essere certi che il contenuto sia nel DOM
        setTimeout(() => {
          focusFirstInPopover()
        }, 0)
      })
      if (useAdvancedMarkers) {
        // Click handling for AdvancedMarkerElement
        marker.addListener('gmp-click', function () {
          infowindow.setContent(contentEl)
          // New open signature with anchor
          infowindow.open({ map: map, anchor: marker })
          // Focus gestito da 'domready'; manteniamo una chiamata di backup
          setTimeout(() => {
            try {
              suppressFocus()
              focusFirstInPopover()
            } catch (e) {}
          }, 50)
          // Set open icon
          if (img) img.src = markerOpenImage
          // Stato attivo
          lastInfoWindow = infowindow
          lastMarker = marker
          lastImg = img
          lastIsAdvanced = true
          lastTriggerEl = nextTriggerEl
          nextTriggerEl = null
          if (typeof A11yUtils !== 'undefined') {
            const msg = bankName
              ? `Dettagli aperti: ${bankName}`
              : 'Dettagli banca aperti sulla mappa'
            A11yUtils.LiveAnnouncer.announce(msg)
          }
        })
        infowindow.addListener('closeclick', function () {
          // Restore default icon on close
          if (img) img.src = markerImage
          if (typeof A11yUtils !== 'undefined') {
            const msg = bankName
              ? `Dettagli chiusi: ${bankName}`
              : 'Dettagli banca chiusi'
            A11yUtils.LiveAnnouncer.announce(msg)
          }
          lastInfoWindow = null
          lastMarker = null
          lastImg = null
          lastIsAdvanced = false
          // Ritorna il focus all'elemento dell'elenco che ha aperto il dettaglio
          if (lastTriggerEl && lastTriggerEl.focus) {
            setTimeout(() => {
              try {
                suppressFocus()
                lastTriggerEl.focus()
              } catch (e) {}
            }, 50)
          }
          lastTriggerEl = null
        })
      } else {
        // Click handling for classic Marker
        marker.addListener('click', function () {
          infowindow.setContent(contentEl)
          // Old signature with marker instance
          if (typeof infowindow.open === 'function') {
            // Support both signatures
            try {
              infowindow.open(map, marker)
            } catch (e) {
              infowindow.open({ map: map, anchor: marker })
            }
          }
          // Focus gestito da 'domready'; manteniamo una chiamata di backup
          setTimeout(() => {
            try {
              suppressFocus()
              focusFirstInPopover()
            } catch (e) {}
          }, 50)
          marker.setIcon && marker.setIcon(markerOpenImage)
          // Stato attivo
          lastInfoWindow = infowindow
          lastMarker = marker
          lastImg = null
          lastIsAdvanced = false
          lastTriggerEl = nextTriggerEl
          nextTriggerEl = null
          if (typeof A11yUtils !== 'undefined') {
            const msg = bankName
              ? `Dettagli aperti: ${bankName}`
              : 'Dettagli banca aperti sulla mappa'
            A11yUtils.LiveAnnouncer.announce(msg)
          }
        })
        infowindow.addListener('closeclick', function () {
          marker.setIcon && marker.setIcon(markerImage)
          if (typeof A11yUtils !== 'undefined') {
            const msg = bankName
              ? `Dettagli chiusi: ${bankName}`
              : 'Dettagli banca chiusi'
            A11yUtils.LiveAnnouncer.announce(msg)
          }
          lastInfoWindow = null
          lastMarker = null
          lastImg = null
          lastIsAdvanced = false
          // Ritorna il focus all'elemento dell'elenco che ha aperto il dettaglio
          if (lastTriggerEl && lastTriggerEl.focus) {
            setTimeout(() => {
              try {
                suppressFocus()
                lastTriggerEl.focus()
              } catch (e) {}
            }, 50)
          }
          lastTriggerEl = null
        })
      }
      return marker
    })
    map.fitBounds(bounds)
    // Routing automatico del focus rimosso per semplicità
    // Helper minimale: elementi interattivi nella mappa (cluster/marker)
    const getInteractiveNodes = () => {
      const container = document.getElementById('banks-map')
      if (!container) return []
      return Array.from(
        container.querySelectorAll('.marker-cluster, .map-adv-marker'),
      )
    }
    // Selezione "virtuale" per tastiera (freccie/Enter) mantenendo focus sul container
    let kbdSelectionIndex = -1
    const announceSelection = (node) => {
      if (typeof A11yUtils === 'undefined') return
      const label =
        node?.getAttribute?.('data-a11y-label') ||
        'Elemento della mappa selezionato'
      A11yUtils.LiveAnnouncer.announce(label)
    }
    const setKbdActive = (node, active) => {
      if (!node) return
      if (active) node.classList.add('kbd-active')
      else node.classList.remove('kbd-active')
    }
    const selectByIndex = (idx, announce = true) => {
      const nodes = getInteractiveNodes()
      if (!nodes.length) return
      if (kbdSelectionIndex >= 0 && kbdSelectionIndex < nodes.length) {
        setKbdActive(nodes[kbdSelectionIndex], false)
      }
      const safeIdx = ((idx % nodes.length) + nodes.length) % nodes.length
      kbdSelectionIndex = safeIdx
      setKbdActive(nodes[safeIdx], true)
      if (announce) announceSelection(nodes[safeIdx])
    }
    const selectFirstInteractiveWithRetry = (attempt = 0) => {
      const nodes = getInteractiveNodes()
      if (nodes.length) {
        selectByIndex(0, true)
      } else if (attempt < 5) {
        setTimeout(() => selectFirstInteractiveWithRetry(attempt + 1), 120)
      }
    }
    // Consenti al Tab di entrare nella mappa e di muoversi tra cluster/marker senza creare trappole
    const mapContainer = document.getElementById('banks-map')
    if (mapContainer) {
      mapContainer.addEventListener('keydown', function (e) {
        const key = e.key
        const nodes = getInteractiveNodes()
        if (!nodes.length) return
        const active = document.activeElement
        const atContainer = e.target === mapContainer
        const isInteractive = nodes.includes(active)

        // Tab navigation lineare senza trapping
        if (key === 'Tab') {
          // Caso 1: Tab dal container verso il primo interattivo
          if (atContainer && !e.shiftKey) {
            // Seleziona il primo ma lascia il focus sul container
            e.preventDefault()
            selectByIndex(0, true)
            return
          }
          // Caso 2: Shift+Tab dal container -> lascia uscire (default)
          if (atContainer && e.shiftKey) return
          // Caso 3: tra elementi interattivi
          // Gli elementi della mappa non sono direttamente focusabili: nessuna gestione extra
          return
        }

        // Navigazione con frecce tra elementi interattivi (ciclica)
        if (
          key === 'ArrowRight' ||
          key === 'ArrowLeft' ||
          key === 'ArrowDown' ||
          key === 'ArrowUp'
        ) {
          if (!atContainer) return
          let idx = kbdSelectionIndex
          if (idx === -1) idx = 0
          const isNext = key === 'ArrowRight' || key === 'ArrowDown'
          const nextIdx =
            (idx + (isNext ? 1 : -1) + nodes.length) % nodes.length
          e.preventDefault()
          selectByIndex(nextIdx, true)
          return
        }

        // Attivazione con Enter/Space sul selezionato
        if (key === 'Enter' || key === ' ') {
          if (!atContainer) return
          const nodesNow = getInteractiveNodes()
          if (!nodesNow.length) return
          const current =
            nodesNow[kbdSelectionIndex !== -1 ? kbdSelectionIndex : 0]
          const inst = current && current.__am
          if (inst) {
            e.preventDefault()
            try {
              if (inst instanceof google.maps.marker.AdvancedMarkerElement) {
                google.maps.event.trigger(inst, 'gmp-click')
              } else {
                google.maps.event.trigger(inst, 'click')
              }
            } catch (err) {
              // Fallback per Marker classico
              current.click && current.click()
            }
          }
          return
        }
      })
      // Permetti anche clic sul container per inviare il focus al primo elemento, se desiderato
      mapContainer.addEventListener('click', function (e) {
        if (e.target === mapContainer) {
          const first = mapContainer.querySelector(
            '.marker-cluster, .map-adv-marker',
          )
          if (first && typeof first.focus === 'function') {
            first.focus()
          }
        }
      })
    }

    if (useAdvancedMarkers) {
      const clusterRenderer = {
        render: function ({ count, position }, stats) {
          // Create a custom DOM element for the Advanced Marker cluster
          const clusterEl = document.createElement('div')
          clusterEl.className = 'marker-cluster'
          // Stile cluster blu come nell'immagine
          clusterEl.style.width = '50px'
          clusterEl.style.height = '50px'
          clusterEl.style.backgroundColor = '#3b5998'
          clusterEl.style.borderRadius = '50%'
          clusterEl.style.display = 'grid'
          clusterEl.style.placeItems = 'center'
          clusterEl.style.color = '#ffffff'
          clusterEl.style.fontWeight = '700'
          clusterEl.style.fontFamily = 'Haffer XH, system-ui, sans-serif'
          clusterEl.style.boxShadow = '0 0 4px rgba(0,0,0,0.4)'
          // Accessibilità: il cluster non è focusabile direttamente; forniamo etichetta per annunci
          clusterEl.setAttribute(
            'data-a11y-label',
            `Raggruppamento di ${count} banche: premi Invio o Spazio per espandere`,
          )
          const labelEl = document.createElement('span')
          labelEl.className = 'marker-cluster-text'
          labelEl.textContent = String(count)
          labelEl.style.fontSize = '26px'
          labelEl.style.fontWeight = '700'
          clusterEl.appendChild(labelEl)

          const clusterMarker = new google.maps.marker.AdvancedMarkerElement({
            position: position,
            content: clusterEl,
            zIndex: Number(google.maps.Marker.MAX_ZINDEX) + count,
          })
          // Collega l'istanza al nodo per gestione tastiera dal container
          clusterEl.__am = clusterMarker
          // Gestione tastiera: attiva click del cluster con Enter/Space
          clusterEl.addEventListener('keydown', function (e) {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              try {
                google.maps.event.trigger(clusterMarker, 'gmp-click')
              } catch (err) {
                // fallback generico
                clusterEl.click()
              }
              // Stabilizza il focus durante lo zoom e poi vai al primo elemento interattivo
              const mapContainer = document.getElementById('banks-map')
              if (mapContainer && typeof mapContainer.focus === 'function') {
                setTimeout(() => mapContainer.focus(), 0)
                // Dopo breve attesa, seleziona il primo interattivo (retry per aggiornamenti DOM)
                setTimeout(() => selectFirstInteractiveWithRetry(0), 500)
              }
            }
          })
          // Click sul cluster: nessun reindirizzamento del focus automatico post-zoom
          clusterEl.addEventListener('click', function () {
            // Stabilizza il focus durante lo zoom e poi vai al primo elemento interattivo
            const mapContainer = document.getElementById('banks-map')
            if (mapContainer && typeof mapContainer.focus === 'function') {
              setTimeout(() => mapContainer.focus(), 0)
              setTimeout(() => selectFirstInteractiveWithRetry(0), 500)
            }
          })

          return clusterMarker
        },
      }
      new markerClusterer.MarkerClusterer({
        map,
        markers,
        renderer: clusterRenderer,
      })
    } else {
      // Default clusterer for classic markers
      new markerClusterer.MarkerClusterer({
        map,
        markers,
      })
      if (!mapId) {
        console.warn(
          'GMAPS_MAP_ID mancante o vuoto: uso Marker classico e clusterer di default. Definisci GMAPS_MAP_ID per abilitare Advanced Marker.',
        )
      }
    }

    // Attiva marker con tastiera dall'elenco (Enter/Space) e chiudi con Escape
    els.on('keydown', function (e) {
      if (e.which === 13 || e.which === 32) {
        const idx = els.index(this)
        const marker = markers[idx]
        const clickEventName = useAdvancedMarkers ? 'gmp-click' : 'click'
        nextTriggerEl = this
        google.maps.event.trigger(marker, clickEventName)
        e.preventDefault()
      }
    })
    jQuery(document).on('keydown', function (e) {
      if (e.key === 'Escape') {
        // Chiudi l'ultimo InfoWindow aperto e ripristina icona se necessario
        if (lastInfoWindow) {
          lastInfoWindow.close()
          if (lastIsAdvanced && lastImg) {
            lastImg.src = markerImage
          } else if (lastMarker && lastMarker.setIcon) {
            lastMarker.setIcon(markerImage)
          }
          if (typeof A11yUtils !== 'undefined') {
            A11yUtils.LiveAnnouncer.announce('Dettagli banca chiusi')
          }
          // Ripristina focus
          if (lastTriggerEl && lastTriggerEl.focus) {
            setTimeout(() => {
              try {
                suppressFocus()
                lastTriggerEl.focus()
              } catch (e) {}
            }, 50)
          }
          lastInfoWindow = null
          lastMarker = null
          lastImg = null
          lastIsAdvanced = false
          lastTriggerEl = null
        }
      }
    })
  },
  docHeight() {
    const updateDocHeight = () => {
      document.documentElement.style.setProperty(
        '--doc-height',
        `${window.innerHeight}px`,
      )
    }
    window.addEventListener('resize', updateDocHeight)
    updateDocHeight()
  },
  enableGalleryBlock: function () {
    jQuery('.block-gallery').each(function () {
      const el = jQuery(this)
      const swiperContainer = jQuery(el).find('.slider')[0]

      const swiper = new Swiper(swiperContainer, {
        wrapperClass: 'slider-wrapper',
        slideClass: 'slide',
        slidesPerView: 1,
        spaceBetween: 0,
        speed: 400,
        loop: true,
        keyboard: {
          enabled: true,
          onlyInViewport: true,
        },
        a11y: {
          enabled: true,
          prevSlideMessage: 'Slide precedente',
          nextSlideMessage: 'Slide successiva',
          firstSlideMessage: 'Questa è la prima slide',
          lastSlideMessage: "Questa è l'ultima slide",
          paginationBulletMessage: 'Vai alla slide {{index}}',
        },
        pagination: {
          el: jQuery(el).find('.slider-nav .pager')[0],
          type: 'custom',
          renderCustom: function (swiper, current, total) {
            const currentFormatted = String(current).padStart(2, '0')
            const totalFormatted = String(total).padStart(2, '0')
            return currentFormatted + ' \\ ' + totalFormatted
          },
        },
        navigation: {
          nextEl: jQuery(el).find('.slider-nav .next')[0],
          prevEl: jQuery(el).find('.slider-nav .prev')[0],
        },
      })

      // Funzione helper per aggiornare l'altezza (definita PRIMA degli eventi)
      swiper.updateHeight = function () {
        const activeSlide = this.slides[this.activeIndex]
        if (activeSlide) {
          // Trova l'immagine o il contenuto nella slide attiva
          const img = activeSlide.querySelector('img')
          const video = activeSlide.querySelector('.video')
          const caption = activeSlide.querySelector('.caption')

          let height = 0
          if (img) {
            // Forza il reflow per ottenere l'altezza corretta
            img.offsetHeight // trigger reflow
            height = img.offsetHeight
            // Fallback a naturalHeight se offsetHeight è 0
            if (height === 0 && img.complete && img.naturalHeight) {
              height = img.naturalHeight
            }
          } else if (video) {
            height = video.offsetHeight
          } else {
            // Fallback: usa scrollHeight della slide
            height = activeSlide.scrollHeight
          }

          // Aggiungi l'altezza della caption se presente
          if (caption && caption.offsetHeight > 0) {
            height += caption.offsetHeight
          }

          if (height > 0) {
            this.wrapperEl.style.height = height + 'px'
          }
        }
      }

      // Setup accessibilità (definita PRIMA degli eventi)
      swiper.setupA11y = function () {
        // Aggiungi role e aria-label al contenitore
        this.el.setAttribute('role', 'region')
        this.el.setAttribute('aria-roledescription', 'galleria')
        this.el.setAttribute('aria-label', 'Galleria immagini')

        // Aggiungi tabindex al contenitore per permettere la navigazione da tastiera
        this.el.setAttribute('tabindex', '0')

        // Aggiungi aria-live per annunciare i cambiamenti
        const wrapperEl = this.wrapperEl
        wrapperEl.setAttribute('aria-live', 'polite')
        wrapperEl.setAttribute('aria-atomic', 'false')
      }

      // Annuncia il cambio slide (definita PRIMA degli eventi)
      swiper.announceSlide = function () {
        const current = this.activeIndex + 1
        const total = this.slides.length
        const message = `Slide ${current} di ${total}`

        if (typeof A11yUtils !== 'undefined') {
          A11yUtils.LiveAnnouncer.announce(message)
        }
      }

      // Ora aggiungiamo gli eventi che usano le funzioni sopra
      swiper.on('init', function () {
        // Imposta altezza iniziale basata sulla slide attiva
        setTimeout(() => this.updateHeight(), 50)
        // Setup accessibilità
        this.setupA11y()
      })

      swiper.on('slideChangeTransitionStart', function () {
        // Aggiorna altezza all'inizio della transizione
        this.updateHeight()
      })

      swiper.on('slideChangeTransitionEnd', function () {
        // Annuncia il cambio slide agli screen reader
        this.announceSlide()
      })

      swiper.on('resize', function () {
        // Aggiorna altezza al resize
        this.updateHeight()
      })

      // Trigger evento init manualmente
      swiper.emit('init')

      // Aggiorna altezza quando le immagini sono caricate
      jQuery(el)
        .find('.slide img')
        .each(function () {
          if (this.complete) {
            setTimeout(() => swiper.updateHeight(), 100)
          } else {
            jQuery(this).on('load', function () {
              swiper.updateHeight()
            })
          }
        })
    })
  },
  initFacetFix: function () {
    document.addEventListener('facetwp-loaded', function () {
      // Attiva lo scroll solo se c'è una query string (filtri/pagina attivi)
      if (!window.location.search) {
        return
      }

      // Archivio Eventi: scroll automatico alla lista eventi
      if (document.body.classList.contains('post-type-archive-evento')) {
        const target = document.getElementById('events-all')
        if (target) {
          const rect = target.getBoundingClientRect()
          const offset = window.pageYOffset + rect.top - 100 // -100 per lasciare h1/header visibile
          window.scrollTo({
            top: offset,
            behavior: 'smooth',
          })
        }
      }

      // Archivio Progetti: stessa logica per la sezione #projects-list
      if (document.body.classList.contains('post-type-archive-progetto')) {
        const target = document.getElementById('projects-list')
        if (target) {
          const rect = target.getBoundingClientRect()
          const offset = window.pageYOffset + rect.top - 100
          window.scrollTo({
            top: offset,
            behavior: 'smooth',
          })
        }
      }
    })
  },
  initHomeHeroSlider: function (slideCount) {
    if (typeof Swiper === 'undefined') return
    var el = document.querySelector('.home-hero-slide.swiper')
    if (!el) return
    var loop = slideCount > 1
    new Swiper(el, {
      slidesPerView: 1,
      loop: loop,
      grabCursor: true,
      wrapperClass: 'swiper-wrapper',
      slideClass: 'swiper-slide',
      spaceBetween: 0,
      autoHeight: true,
      breakpoints: {
        0: { autoHeight: true },
        768: { autoHeight: false },
      },
      pagination: {
        el: '.swiper-pagination',
        clickable: true,
        bulletElement: 'button',
        type: 'bullets',
        dynamicBullets: true,
        dynamicMainBullets: 1,
        renderBullet: function (index, className) {
          return `<button class="${className}" type="button" role="tab" aria-label="Immagine ${
            index + 1
          }"></button>`
        },
      },
      autoplay: {
        delay: 10000,
        disableOnInteraction: true,
        pauseOnMouseEnter: true,
      },
      a11y: {
        enabled: true,
        prevSlideMessage: 'Slide precedente',
        nextSlideMessage: 'Slide successiva',
        firstSlideMessage: 'Questa è la prima slide',
        lastSlideMessage: 'Questa è l’ultima slide',
      },
      keyboard: {
        enabled: true,
        onlyInViewport: true,
      },
    })
  },
}

jQuery(function () {
  app.init()
})

// Callback globale richiamato da Google Maps JS API (?callback=fedlusInitMap)
window.fedlusInitMap = function () {
  try {
    if (typeof markerClusterer === 'undefined') {
      // MarkerClusterer arriva da un altro script; se non è ancora pronto, ritenta fra poco
      setTimeout(window.fedlusInitMap, 50)
      return
    }
    if (jQuery && jQuery('#banks-map').length > 0) {
      app.createMap()
    }
    if (jQuery && jQuery('#single-bank-map').length > 0) {
      app.createSingleBankMap()
    }
  } catch (e) {
    console.error('Errore in fedlusInitMap:', e)
  }
};
